/**
 * ENHANCED LEAD TRACKING WITH USER JOURNEY & SCROLL TRACKING
 * Captures complete user behavior, journey, and engagement metrics
 */

(function() {
  'use strict';

  const NAMESPACE = '__leadTracking__';
  
  if (window[NAMESPACE]) {
    console.warn('Lead Tracking System already initialized');
    return;
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  
  const TRACKING_PARAMS = {
    google_ads: ['gclid', 'gclsrc', 'gad_source', 'gbraid', 'wbraid', 'dclid'],
    google_shopping: ['srsltid'],
    google_custom: ['hsa_acc', 'hsa_cam', 'hsa_grp', 'hsa_ad', 'hsa_src', 'hsa_tgt', 'hsa_kw', 'hsa_mt', 'hsa_net', 'hsa_ver'],
    facebook: ['fbclid', 'fb_action_ids', 'fb_action_types', 'fb_source'],
    microsoft: ['msclkid'],
    linkedin: ['li_fat_id'],
    twitter: ['twclid'],
    tiktok: ['ttclid'],
    pinterest: ['epik'],
    snapchat: ['ScCid'],
    utm: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'],
    other: ['ref', 'source', 'campaign']
  };

  const ALL_PARAMS = Object.values(TRACKING_PARAMS).flat();
  const STORAGE_PREFIX = '__lt_';
  const MAX_JOURNEY_PAGES = 50; // Limit journey history to prevent storage overflow

  // ============================================================================
  // STORAGE MANAGER
  // ============================================================================
  
  const StorageManager = {
    memoryStorage: {},
    
    _prefixKey: function(key) {
      return STORAGE_PREFIX + key;
    },
    
    isStorageAvailable: function(type) {
      try {
        const storage = window[type];
        const test = '__storage_test__';
        storage.setItem(test, test);
        storage.removeItem(test);
        return true;
      } catch(e) {
        return false;
      }
    },
    
    set: function(key, value) {
      const prefixedKey = this._prefixKey(key);
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      
      if (this.isStorageAvailable('localStorage')) {
        try {
          localStorage.setItem(prefixedKey, stringValue);
          return true;
        } catch(e) {}
      }
      
      if (this.isStorageAvailable('sessionStorage')) {
        try {
          sessionStorage.setItem(prefixedKey, stringValue);
          return true;
        } catch(e) {}
      }
      
      this.memoryStorage[prefixedKey] = stringValue;
      return true;
    },
    
    get: function(key) {
      const prefixedKey = this._prefixKey(key);
      
      if (this.isStorageAvailable('localStorage')) {
        try {
          const value = localStorage.getItem(prefixedKey);
          if (value !== null) return value;
        } catch(e) {}
      }
      
      if (this.isStorageAvailable('sessionStorage')) {
        try {
          const value = sessionStorage.getItem(prefixedKey);
          if (value !== null) return value;
        } catch(e) {}
      }
      
      return this.memoryStorage[prefixedKey] || null;
    },
    
    getJSON: function(key) {
      const value = this.get(key);
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch(e) {
        return value;
      }
    }
  };

  // ============================================================================
  // SCREENSHOT EVIDENCE COLLECTOR
  // ============================================================================
  
  const ScreenshotEvidence = {
    
    captureURLEvidence: function() {
      return {
        full_url: window.location.href,
        has_google_ads_marker: window.location.href.includes('gclid=') || window.location.href.includes('srsltid='),
        has_facebook_marker: window.location.href.includes('fbclid='),
        has_utm_markers: window.location.href.includes('utm_'),
        url_length: window.location.href.length,
        query_string: window.location.search
      };
    },
    
    captureAdClickEvidence: function() {
      const urlParams = new URLSearchParams(window.location.search);
      const evidence = {
        came_from_google_ad: false,
        came_from_google_shopping: false,
        came_from_facebook_ad: false,
        came_from_paid_campaign: false,
        evidence_markers: []
      };
      
      if (urlParams.get('gclid')) {
        evidence.came_from_google_ad = true;
        evidence.evidence_markers.push({
          type: 'google_ads',
          marker: 'gclid',
          value: urlParams.get('gclid'),
          proof_level: 'DEFINITIVE',
          explanation: 'Google Click ID (GCLID) is only added when user clicks a Google Ad'
        });
      }
      
      if (urlParams.get('srsltid')) {
        evidence.came_from_google_shopping = true;
        evidence.evidence_markers.push({
          type: 'google_shopping',
          marker: 'srsltid',
          value: urlParams.get('srsltid'),
          proof_level: 'DEFINITIVE',
          explanation: 'Shopping Results Link Tracking ID is only added when user clicks a Google Shopping result'
        });
      }
      
      if (urlParams.get('fbclid')) {
        evidence.came_from_facebook_ad = true;
        evidence.evidence_markers.push({
          type: 'facebook_ads',
          marker: 'fbclid',
          value: urlParams.get('fbclid'),
          proof_level: 'DEFINITIVE',
          explanation: 'Facebook Click ID is only added when user clicks a Facebook Ad'
        });
      }
      
      if (urlParams.get('utm_source') && urlParams.get('utm_medium')) {
        const medium = urlParams.get('utm_medium').toLowerCase();
        if (medium === 'cpc' || medium === 'ppc' || medium === 'paid') {
          evidence.came_from_paid_campaign = true;
          evidence.evidence_markers.push({
            type: 'utm_campaign',
            marker: 'utm_source + utm_medium',
            value: `${urlParams.get('utm_source')} / ${urlParams.get('utm_medium')}`,
            proof_level: 'PROBABLE',
            explanation: 'UTM parameters suggest paid campaign, but could be manually added or shared link'
          });
        }
      }
      
      return evidence;
    }
  };

  // ============================================================================
  // TRACKING DATA COLLECTOR
  // ============================================================================
  
  const TrackingCollector = {
    
    collectAllURLParams: function() {
      const params = new URLSearchParams(window.location.search);
      const allParams = {};
      params.forEach((value, key) => {
        allParams[key] = value;
      });
      return allParams;
    },
    
    collectURLParams: function() {
      const params = new URLSearchParams(window.location.search);
      const collected = {};
      ALL_PARAMS.forEach(key => {
        const value = params.get(key);
        if (value) {
          collected[key] = value;
        }
      });
      return collected;
    },
    
    collectReferrerData: function() {
      const referrer = document.referrer;
      
      if (!referrer) {
        return {
          referrer: 'direct',
          referrer_domain: 'direct',
          referrer_source_type: 'direct',
          referrer_description: 'User typed the website address directly or used a bookmark'
        };
      }
      
      try {
        const url = new URL(referrer);
        const domain = url.hostname.replace('www.', '');
        
        let sourceType = 'referral';
        let description = `User came from ${domain}`;
        
        if (domain.includes('google.')) {
          sourceType = 'google_organic';
          description = 'User found you by searching on Google (not an ad)';
        } else if (domain.includes('facebook.') || domain.includes('fb.')) {
          sourceType = 'facebook_organic';
          description = 'User came from Facebook (not an ad)';
        } else if (domain.includes('instagram.')) {
          sourceType = 'instagram';
          description = 'User came from Instagram';
        } else if (domain.includes('linkedin.')) {
          sourceType = 'linkedin';
          description = 'User came from LinkedIn';
        } else if (domain.includes('twitter.') || domain.includes('x.com')) {
          sourceType = 'twitter';
          description = 'User came from Twitter/X';
        } else if (domain.includes('youtube.')) {
          sourceType = 'youtube';
          description = 'User came from YouTube';
        } else if (domain.includes('bing.')) {
          sourceType = 'bing_organic';
          description = 'User found you by searching on Bing (not an ad)';
        } else if (domain.includes('yahoo.')) {
          sourceType = 'yahoo_organic';
          description = 'User found you by searching on Yahoo (not an ad)';
        } else if (domain.includes('tiktok.')) {
          sourceType = 'tiktok';
          description = 'User came from TikTok';
        } else if (domain.includes('pinterest.')) {
          sourceType = 'pinterest';
          description = 'User came from Pinterest';
        }
        
        return {
          referrer: referrer,
          referrer_domain: domain,
          referrer_source_type: sourceType,
          referrer_description: description
        };
      } catch(e) {
        return {
          referrer: referrer,
          referrer_domain: 'unknown',
          referrer_source_type: 'referral',
          referrer_description: 'User came from another website'
        };
      }
    },
    
    collectBrowserData: function() {
      const ua = navigator.userAgent;
      
      let browser = 'unknown';
      let browserName = 'Unknown Browser';
      if (ua.includes('Chrome') && !ua.includes('Edg')) {
        browser = 'chrome';
        browserName = 'Google Chrome';
      } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
        browser = 'safari';
        browserName = 'Safari';
      } else if (ua.includes('Firefox')) {
        browser = 'firefox';
        browserName = 'Firefox';
      } else if (ua.includes('Edg')) {
        browser = 'edge';
        browserName = 'Microsoft Edge';
      }
      
      let device = 'desktop';
      let deviceName = 'Desktop Computer';
      if (/Mobile|Android|iPhone|iPod/i.test(ua)) {
        device = 'mobile';
        deviceName = 'Mobile Phone';
      }
      if (/iPad|Tablet/i.test(ua)) {
        device = 'tablet';
        deviceName = 'Tablet';
      }
      
      let os = 'unknown';
      let osName = 'Unknown OS';
      if (ua.includes('Windows')) {
        os = 'windows';
        osName = 'Windows';
      } else if (ua.includes('Mac')) {
        os = 'macos';
        osName = 'Mac';
      } else if (ua.includes('Linux')) {
        os = 'linux';
        osName = 'Linux';
      } else if (ua.includes('Android')) {
        os = 'android';
        osName = 'Android';
      } else if (ua.includes('iPhone') || ua.includes('iPad')) {
        os = 'ios';
        osName = 'iPhone/iPad';
      }
      
      const trackingStatus = [];
      let trackingDescription = 'Normal tracking';
      
      if (browser === 'safari') {
        trackingStatus.push('safari_itp');
        trackingDescription = '⚠️ Safari blocks tracking after 7 days - tracking may be limited';
      }
      if (!StorageManager.isStorageAvailable('localStorage')) {
        trackingStatus.push('localstorage_blocked');
        trackingDescription = '⚠️ Browser is blocking storage - tracking may be limited';
      }
      if (typeof navigator.doNotTrack !== 'undefined' && navigator.doNotTrack === '1') {
        trackingStatus.push('dnt_enabled');
        trackingDescription = '⚠️ "Do Not Track" is enabled - tracking may be limited';
      }
      
      return {
        user_agent: ua,
        browser: browser,
        browser_name: browserName,
        device: device,
        device_name: deviceName,
        os: os,
        os_name: osName,
        tracking_status: trackingStatus.join(',') || 'full_tracking',
        tracking_description: trackingDescription,
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language || navigator.userLanguage,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    },
    
    collectPageData: function() {
      return {
        landing_page: window.location.href,
        landing_page_path: window.location.pathname,
        landing_page_query: window.location.search,
        page_title: document.title
      };
    },
    
    getTimestamp: function() {
      const now = new Date();
      return {
        timestamp: now.toISOString(),
        timestamp_local: now.toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZoneName: 'short'
        }),
        timestamp_unix: Math.floor(now.getTime() / 1000),
        timestamp_readable: now.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      };
    },
    
    generateVisitorID: function() {
      return 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },
    
    collectAll: function() {
      const allURLParams = this.collectAllURLParams();
      const trackingParams = this.collectURLParams();
      const referrerData = this.collectReferrerData();
      const browserData = this.collectBrowserData();
      const pageData = this.collectPageData();
      const timestamp = this.getTimestamp();
      const urlEvidence = ScreenshotEvidence.captureURLEvidence();
      const adClickEvidence = ScreenshotEvidence.captureAdClickEvidence();
      
      return {
        url_params: allURLParams,
        ...trackingParams,
        ...referrerData,
        ...browserData,
        ...pageData,
        ...timestamp,
        url_evidence: urlEvidence,
        ad_click_evidence: adClickEvidence,
        has_tracking_params: Object.keys(trackingParams).length > 0
      };
    }
  };

  // ============================================================================
  // ATTRIBUTION LOGIC
  // ============================================================================
  
  const Attribution = {
    
    determineSource: function(data) {
      if (data.gclid) return 'google_ads';
      if (data.srsltid) return 'google_shopping';
      if (data.fbclid) return 'facebook_ads';
      if (data.msclkid) return 'microsoft_ads';
      if (data.li_fat_id) return 'linkedin_ads';
      if (data.twclid) return 'twitter_ads';
      if (data.ttclid) return 'tiktok_ads';
      
      if (data.utm_source) {
        const source = data.utm_source.toLowerCase();
        const medium = (data.utm_medium || '').toLowerCase();
        
        if (medium === 'cpc' || medium === 'ppc' || medium === 'paid') {
          return source + '_paid';
        }
        return source;
      }
      
      if (data.referrer_source_type && data.referrer_source_type !== 'direct') {
        return data.referrer_source_type;
      }
      
      return 'direct';
    },
    
    determineConfidence: function(data) {
      if (data.gclid || data.srsltid || data.fbclid || data.msclkid || 
          data.li_fat_id || data.twclid || data.ttclid) {
        return 'high';
      }
      
      if (data.utm_source && data.utm_medium) {
        return 'medium';
      }
      
      if (data.referrer && data.referrer !== 'direct') {
        return 'low';
      }
      
      return 'none';
    },
    
    getPlainEnglishExplanation: function(data) {
      const confidence = this.determineConfidence(data);
      const source = this.determineSource(data);
      
      let explanation = {
        simple_answer: '',
        why: '',
        proof_level: '',
        color: ''
      };
      
      if (data.gclid) {
        explanation.simple_answer = '✅ YES - This person clicked your Google Ad';
        explanation.why = `We know because the website address (URL) contains a special Google tracking code called "gclid". This code is ONLY added by Google when someone clicks an ad. It's impossible to fake.`;
        explanation.proof_level = 'DEFINITIVE PROOF';
        explanation.color = 'green';
      } else if (data.srsltid) {
        explanation.simple_answer = '✅ YES - This person clicked your Google Shopping result';
        explanation.why = `We know because the URL contains a special Google Shopping tracking code. This is ONLY added when someone clicks a shopping result.`;
        explanation.proof_level = 'DEFINITIVE PROOF';
        explanation.color = 'green';
      } else if (data.fbclid) {
        explanation.simple_answer = '✅ YES - This person clicked your Facebook Ad';
        explanation.why = `We know because the URL contains Facebook's tracking code. This is ONLY added when someone clicks a Facebook ad.`;
        explanation.proof_level = 'DEFINITIVE PROOF';
        explanation.color = 'green';
      } else if (data.utm_source && data.utm_medium) {
        explanation.simple_answer = `⚠️ PROBABLY - This might be from ${data.utm_source}`;
        explanation.why = `The URL has campaign tracking codes (utm_source=${data.utm_source}), but NO Google tracking code. This could mean: (1) They clicked an ad but Apple's Safari or an ad blocker removed the tracking code, OR (2) Someone shared this link via text/email/WhatsApp.`;
        explanation.proof_level = 'PROBABLE - BUT NOT 100% CERTAIN';
        explanation.color = 'yellow';
      } else if (data.referrer && data.referrer !== 'direct') {
        explanation.simple_answer = `❓ MAYBE - ${data.referrer_description}`;
        explanation.why = `They came from ${data.referrer_domain}, but there's no ad tracking code. This is most likely organic (not from an ad).`;
        explanation.proof_level = 'LOW CONFIDENCE';
        explanation.color = 'orange';
      } else {
        explanation.simple_answer = '❌ NO - We cannot prove this came from any ad';
        explanation.why = `There are NO tracking codes in the URL. This means: (1) They typed your website address directly, (2) They used a bookmark, (3) Someone shared your link via text/email, (4) Apple Safari removed the tracking code, OR (5) They have an ad blocker.`;
        explanation.proof_level = 'NO PROOF AVAILABLE';
        explanation.color = 'red';
        
        if (data.tracking_status && data.tracking_status !== 'full_tracking') {
          explanation.why += `\n\nIMPORTANT: We detected that ${data.tracking_description}. This is why we have no tracking data.`;
        }
      }
      
      return explanation;
    }
  };

  // ============================================================================
  // SCROLL TRACKING
  // ============================================================================
  
  const ScrollTracker = {
    maxScrollDepth: 0,
    scrollEvents: [],
    lastScrollTime: null,
    scrollStartTime: null,
    isTracking: false,
    
    init: function() {
      this.scrollStartTime = Date.now();
      this.lastScrollTime = Date.now();
      this.isTracking = true;
      
      let scrollTimeout;
      const handleScroll = () => {
        if (!this.isTracking) return;
        
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
        
        if (scrollPercent > this.maxScrollDepth) {
          this.maxScrollDepth = scrollPercent;
        }
        
        this.lastScrollTime = Date.now();
        
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.recordScrollMilestone(scrollPercent);
        }, 500);
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
    },
    
    recordScrollMilestone: function(percent) {
      const milestones = [25, 50, 75, 100];
      const timestamp = Date.now();
      
      milestones.forEach(milestone => {
        const alreadyRecorded = this.scrollEvents.some(e => e.milestone === milestone);
        if (!alreadyRecorded && percent >= milestone) {
          this.scrollEvents.push({
            milestone: milestone,
            timestamp: new Date().toISOString(),
            time_from_page_load: Math.round((timestamp - this.scrollStartTime) / 1000)
          });
        }
      });
    },
    
    getScrollData: function() {
      const totalTime = this.lastScrollTime - this.scrollStartTime;
      const scrollHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      
      return {
        max_scroll_depth_percent: this.maxScrollDepth,
        scroll_depth_description: this.getScrollDescription(this.maxScrollDepth),
        did_scroll: this.maxScrollDepth > 0,
        reached_bottom: this.maxScrollDepth >= 95,
        scroll_milestones_reached: this.scrollEvents,
        total_page_height: scrollHeight,
        viewport_height: viewportHeight,
        scrollable_height: scrollHeight - viewportHeight,
        time_spent_scrolling_seconds: Math.round(totalTime / 1000),
        last_scroll_timestamp: new Date(this.lastScrollTime).toISOString()
      };
    },
    
    getScrollDescription: function(percent) {
      if (percent === 0) return 'No scrolling - user stayed at top';
      if (percent < 25) return 'Minimal engagement - only saw the top';
      if (percent < 50) return 'Low engagement - saw less than half';
      if (percent < 75) return 'Medium engagement - saw more than half';
      if (percent < 95) return 'High engagement - read most of the page';
      return 'Complete engagement - scrolled to the bottom';
    }
  };

  // ============================================================================
  // PAGE JOURNEY TRACKER
  // ============================================================================
  
  const JourneyTracker = {
    
    getJourney: function() {
      return StorageManager.getJSON('page_journey') || [];
    },
    
    addPageToJourney: function(pageData) {
      let journey = this.getJourney();
      
      const pageEntry = {
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        page_query: window.location.search,
        url_params_on_this_page: TrackingCollector.collectAllURLParams(),
        tracking_params_on_this_page: TrackingCollector.collectURLParams(),
        had_tracking_params: Object.keys(TrackingCollector.collectURLParams()).length > 0,
        params_status: this.analyzeParamsStatus(),
        visited_at: new Date().toISOString(),
        visited_at_readable: new Date().toLocaleString(),
        referrer_for_this_page: document.referrer || 'direct_navigation',
        page_number_in_journey: journey.length + 1
      };
      
      journey.push(pageEntry);
      
      // Limit journey size
      if (journey.length > MAX_JOURNEY_PAGES) {
        journey = journey.slice(-MAX_JOURNEY_PAGES);
      }
      
      StorageManager.set('page_journey', journey);
      return journey;
    },
    
    analyzeParamsStatus: function() {
      const currentParams = TrackingCollector.collectURLParams();
      const hasParams = Object.keys(currentParams).length > 0;
      
      if (!hasParams) {
        return {
          status: 'no_params',
          description: 'No tracking parameters on this page',
          params_were_removed: this.wereParamsRemovedOnThisPage()
        };
      }
      
      return {
        status: 'has_params',
        description: 'Tracking parameters present on this page',
        param_count: Object.keys(currentParams).length,
        params: currentParams
      };
    },
    
    wereParamsRemovedOnThisPage: function() {
      const journey = this.getJourney();
      if (journey.length === 0) return false;
      
      const previousPage = journey[journey.length - 1];
      const currentParams = TrackingCollector.collectURLParams();
      
      return previousPage.had_tracking_params && Object.keys(currentParams).length === 0;
    },
    
    getJourneySummary: function() {
      const journey = this.getJourney();
      
      if (journey.length === 0) {
        return {
          total_pages_visited: 0,
          journey_description: 'No pages tracked yet'
        };
      }
      
      const firstPage = journey[0];
      const lastPage = journey[journey.length - 1];
      const pagesWithParams = journey.filter(p => p.had_tracking_params).length;
      const paramsLostOnPage = journey.findIndex((p, i) => {
        return i > 0 && journey[i - 1].had_tracking_params && !p.had_tracking_params;
      });
      
      return {
        total_pages_visited: journey.length,
        entry_page: {
          url: firstPage.page_url,
          path: firstPage.page_path,
          title: firstPage.page_title,
          had_params: firstPage.had_tracking_params,
          params: firstPage.tracking_params_on_this_page
        },
        current_page: {
          url: lastPage.page_url,
          path: lastPage.page_path,
          title: lastPage.page_title,
          had_params: lastPage.had_tracking_params
        },
        pages_with_tracking_params: pagesWithParams,
        params_lost_on_page_number: paramsLostOnPage !== -1 ? paramsLostOnPage + 1 : null,
        params_lost_on_page_url: paramsLostOnPage !== -1 ? journey[paramsLostOnPage].page_url : null,
        full_journey: journey.map(p => ({
          page: p.page_path,
          title: p.page_title,
          had_params: p.had_tracking_params,
          visited_at: p.visited_at_readable
        }))
      };
    }
  };

  // ============================================================================
  // SESSION TRACKING
  // ============================================================================
  
  const SessionTracker = {
    
    trackVisit: function() {
      let visitCount = StorageManager.get('visit_count');
      visitCount = visitCount ? parseInt(visitCount, 10) + 1 : 1;
      StorageManager.set('visit_count', visitCount);
      return visitCount;
    },
    
    trackFirstVisit: function(currentData) {
      const existing = StorageManager.getJSON('first_visit_data');
      if (existing) return existing;
      
      const firstVisitData = {
        ...currentData,
        first_visit_timestamp: currentData.timestamp,
        first_visit_timestamp_local: currentData.timestamp_local,
        entry_url: window.location.href,
        entry_path: window.location.pathname,
        entry_query: window.location.search,
        entry_params: TrackingCollector.collectAllURLParams()
      };
      
      StorageManager.set('first_visit_data', firstVisitData);
      return firstVisitData;
    },
    
    trackFirstClick: function() {
      if (StorageManager.get('first_click_timestamp')) return;
      
      const recordFirstClick = () => {
        const clickData = TrackingCollector.getTimestamp();
        StorageManager.set('first_click_timestamp', clickData.timestamp);
        StorageManager.set('first_click_timestamp_local', clickData.timestamp_local);
        window.removeEventListener('click', recordFirstClick);
      };
      
      window.addEventListener('click', recordFirstClick, { once: true });
    }
  };

  // ============================================================================
  // ENGAGEMENT TRACKER
  // ============================================================================
  
  const EngagementTracker = {
    pageLoadTime: null,
    
    init: function() {
      this.pageLoadTime = Date.now();
    },
    
    getTimeOnPage: function() {
      const now = Date.now();
      const timeSeconds = Math.round((now - this.pageLoadTime) / 1000);
      
      return {
        time_on_current_page_seconds: timeSeconds,
        time_on_current_page_minutes: Math.round(timeSeconds / 60 * 10) / 10,
        time_description: this.getTimeDescription(timeSeconds)
      };
    },
    
    getTimeDescription: function(seconds) {
      if (seconds < 10) return 'Bounce risk - very short visit';
      if (seconds < 30) return 'Quick visit - limited engagement';
      if (seconds < 60) return 'Brief visit - some interest';
      if (seconds < 180) return 'Good engagement - reading content';
      if (seconds < 300) return 'Strong engagement - interested user';
      return 'Very high engagement - deeply interested';
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  function init() {
    // Initialize engagement and scroll tracking
    EngagementTracker.init();
    ScrollTracker.init();
    
    // Get or create visitor ID
    let visitorID = StorageManager.get('visitor_id');
    if (!visitorID) {
      visitorID = TrackingCollector.generateVisitorID();
      StorageManager.set('visitor_id', visitorID);
    }
    
    // Track visit count
    const visitCount = SessionTracker.trackVisit();
    
    // Collect current page data
    const currentData = TrackingCollector.collectAll();
    currentData.visitor_id = visitorID;
    currentData.visit_count = visitCount;
    
    // Get or create first visit data
    let firstVisitData = StorageManager.getJSON('first_visit_data');
    if (!firstVisitData) {
      currentData.attribution_source = Attribution.determineSource(currentData);
      currentData.attribution_confidence = Attribution.determineConfidence(currentData);
      currentData.plain_english_explanation = Attribution.getPlainEnglishExplanation(currentData);
      
      firstVisitData = SessionTracker.trackFirstVisit(currentData);
    }
    
    // Add current page to journey
    JourneyTracker.addPageToJourney(currentData);
    
    // Track first click
    SessionTracker.trackFirstClick();
    
    // Get stored timestamps
    const firstClickTimestamp = StorageManager.get('first_click_timestamp');
    const firstClickTimestampLocal = StorageManager.get('first_click_timestamp_local');
    
    // Prepare current page attribution
    currentData.attribution_source = Attribution.determineSource(currentData);
    currentData.attribution_confidence = Attribution.determineConfidence(currentData);
    
    // Create metadata object
    const metadata = {
      first_touch: firstVisitData,
      current: currentData,
      first_click_timestamp: firstClickTimestamp,
      first_click_timestamp_local: firstClickTimestampLocal,
      visitor_id: visitorID,
      visit_count: visitCount,
      attribution: {
        source: firstVisitData.attribution_source,
        confidence: firstVisitData.attribution_confidence,
        first_touch_source: firstVisitData.attribution_source,
        last_touch_source: currentData.attribution_source || firstVisitData.attribution_source,
        plain_english: firstVisitData.plain_english_explanation
      }
    };
    
    // Create dynamic getter function that returns fresh data
    const getLiveMetadata = function() {
      const scrollData = ScrollTracker.getScrollData();
      const engagementData = EngagementTracker.getTimeOnPage();
      const journeySummary = JourneyTracker.getJourneySummary();
      const currentPageData = TrackingCollector.collectAll();
      
      return {
        ...metadata,
        engagement: {
          ...engagementData,
          ...scrollData
        },
        journey: journeySummary,
        last_page: {
          url: window.location.href,
          path: window.location.pathname,
          title: document.title,
          query: window.location.search,
          current_params: TrackingCollector.collectAllURLParams()
        },
        summary: {
          entry_url: firstVisitData.entry_url || firstVisitData.landing_page,
          entry_had_tracking_params: firstVisitData.has_tracking_params,
          current_url: window.location.href,
          total_pages_visited: journeySummary.total_pages_visited,
          time_on_site_seconds: engagementData.time_on_current_page_seconds,
          max_scroll_depth: scrollData.max_scroll_depth_percent,
          user_engaged: scrollData.max_scroll_depth_percent > 25 || engagementData.time_on_current_page_seconds > 30,
          params_were_lost: journeySummary.params_lost_on_page_number !== null
        }
      };
    };
    // Function to clear tracking data
    const clearTrackingData = function() {
      // Remove all tracking data from storage
      const keysToRemove = [
        'visitor_id',
        'visit_count',
        'first_visit_data',
        'first_click_timestamp',
        'first_click_timestamp_local',
        'page_journey'
      ];
      
      keysToRemove.forEach(key => {
        const prefixedKey = STORAGE_PREFIX + key;
        if (StorageManager.isStorageAvailable('localStorage')) {
          try {
            localStorage.removeItem(prefixedKey);
          } catch(e) {}
        }
        if (StorageManager.isStorageAvailable('sessionStorage')) {
          try {
            sessionStorage.removeItem(prefixedKey);
          } catch(e) {}
        }
        delete StorageManager.memoryStorage[prefixedKey];
      });
      
      return {
        status: 'success',
        message: 'All tracking data has been cleared',
        cleared_at: new Date().toISOString()
      };
    };
    
    // Expose to window
    window[NAMESPACE] = {
      version: '2.0.0',
      getMetadata: getLiveMetadata,
      clearData: clearTrackingData,
      metadata: metadata // Keep for backward compatibility
    };
    
    // Expose convenience functions
    window.getLeadTrackingData = getLiveMetadata; // Function reference
    window.getLeadTrackingJSON = function() {
      return JSON.stringify(getLiveMetadata(), null, 2);
    };
    window.clearLeadTrackingData = clearTrackingData;
    
    // Auto-update property (for backward compatibility)
    Object.defineProperty(window, 'leadTrackingMetadata', {
      get: function() {
        return getLiveMetadata();
      },
      configurable: true
    });
  }

  // ============================================================================
  // START TRACKING
  // ============================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();